<template>
  <div :class="themeClass" class="multiselect-block">
    <div :id="wrapperId" class="multiselect-wrapper" :class="multiselectClass">
      <Multiselect
        :id="id"
        ref="select"
        v-model="selectValue"
        class="multiselect"
        :options="options"
        :options-limit="optionsLimit"
        :label="itemLabel"
        :group-label="groupLabel"
        :group-values="groupValues"
        :track-by="valueKey"
        :open-direction="openDirection"
        select-label=""
        deselect-label=""
        selected-label=""
        :hide-selected="false"
        :disabled="disabled"
        :placeholder="placeholder"
        :allow-empty="false"
        :loading="isLoading"
        :searchable="searchable"
        :data-cy="dataCy"
        :class="hideInactiveElement(value, options)"
        :option-height="optionHeight"
        @open="onOpen"
        @close="onClose"
        @search-change="onSearchChange"
        @remove="removeValue"
        @input="onInput"
      >
        <template #singleLabel="props">
          <!-- @slot Use it to add single label. -->
          <slot :scope-props="props" name="singleLabel" />
          <span v-if="!isShownSingleLabelSlot" class="multiselect-single-slot">
            {{ props.option[itemLabel] }}
          </span>

          <span>
            <!-- @slot Use it to add after single label. -->
            <slot :scope-props="props" name="singleLabelAfter" />
          </span>
        </template>

        <template slot="option" slot-scope="props">
          <!-- @slot Use it to add option. -->
          <slot :scope-props="props" name="option" />

          <div v-if="groupValues && !isShownOptionSlot">
            <div v-if="props.option.$groupLabel">
              {{ props.option.$groupLabel }}
            </div>

            <div
              v-else-if="props.option.isSubGroup"
              class="group-label"
              :style="leftMarginForSubCategory(props.option.level)"
            >
              {{ props.option[itemLabel] }}
            </div>

            <div v-else :style="leftMarginForSubCategory(props.option.level)" class="select-label">
              <!-- @slot Use it to add something before option. -->
              <slot :option="props.option" name="beforeOption"></slot>
              {{ props.option[itemLabel] }}
            </div>
          </div>
          <div v-else class="select-label">
            <!-- @slot Use it to add something before option. -->
            <slot :option="props.option" name="beforeOption"></slot>
            {{ props.option[itemLabel] }}
          </div>
        </template>

        <template #noResult>
          {{ i18n.listIsEmpty }}
        </template>

        <template #noOptions>
          {{ i18n.noDataToShow }}
        </template>

        <template #caret="{ toggle }">
          <div class="multiselect-caret-down" @mousedown.prevent.stop="toggle">
            <OurSvgIcon
              interactive
              :size="iconSize"
              color="gray"
              :src="icons.AngleDown"
              class="icon"
              :data-cy="`${dataCy}-caret`"
            />
          </div>

          <div v-if="selectValue && allowEmpty && !disabled" class="multiselect-caret-close">
            <OurSvgIcon
              interactive
              :size="iconSize"
              color="gray"
              :src="icons.Close"
              :data-cy="`${dataCy}-close`"
              @mousedown.native="[onMousedownRemoveValue(), toggle()]"
            />
          </div>
        </template>

        <template v-if="addOption" #afterList>
          <div class="multiselect-add-wrapper" @click="onClickAddOption">
            <div class="multiselect-add-title">
              {{ i18n.add }}
              <span class="multiselect-add-title-command">{{ addOptionKeyCombination }}</span>
            </div>

            <OurButton ref="addOptionButton" class="multiselect-add-button">
              <OurSvgIcon
                :src="icons.Plus2"
                class="multiselect-add-icon"
                color="white"
                pill
                size="2xs"
              />
            </OurButton>
          </div>
        </template>
      </Multiselect>

      <label v-if="label" class="label" :class="labelClasses" :for="id" :title="label">
        {{ label }}
        <slot name="after-label" />
      </label>

      <div v-if="isShownAfterCaretSlot" class="after-caret-slot">
        <!-- @slot Use it to add something after caret. -->
        <slot name="after-caret" />
      </div>
    </div>

    <div v-if="description && !error" class="description">
      {{ description }}
    </div>

    <p v-if="error && !value.length" class="error-message" :data-cy="`${dataCy}-error-message`">
      {{ error }}
    </p>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import { getThemeClass, getRandomId, isMac, PX_IN_REM } from "@/services/_bit/UiService";
import I18nServiceDefault from "@/services/_bit/I18nService";
import debounce from "lodash.debounce";

import OurButton from "@/components/_bit/Button";
import OurSvgIcon from "@/components/_bit/IconSvg";

export default {
  name: "OurSelect",
  components: {
    Multiselect,
    OurButton,
    OurSvgIcon,
  },

  props: {
    /**
     * Set input select value.
     */
    value: {
      type: [String, Number, Boolean],
      default: "",
    },

    /**
     * Set options for component.
     */
    options: {
      type: Array,
      default: () => [],
    },

    /**
     * Set component label.
     */
    label: {
      type: String,
      default: "",
    },

    /**
     * Label from option Object, that will be visible in the dropdown.
     */
    itemLabel: {
      type: String,
      default: "label",
    },

    /**
     * Set input placeholder.
     */
    placeholder: {
      type: String,
      default: "",
    },

    /**
     * Set error message.
     */
    error: {
      type: String,
      default: "",
    },

    /**
     * Make input inactive.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows clear selected value.
     */
    allowEmpty: {
      type: Boolean,
      default: true,
    },

    /**
     * Set a name of the property containing the group label.
     */
    groupLabel: {
      type: String,
      default: "label",
    },

    /**
     * Set a name of the property containing the group values.
     */
    groupValues: {
      type: String,
      default: "",
    },

    /**
     * Set limits the options displayed in the dropdown to the first X options.
     */
    optionsLimit: {
      type: Number,
      default: 500,
    },

    /**
     * Set track value key.
     */
    valueKey: {
      type: String,
      default: "id",
    },

    /**
     * Fixed opening direction (instead of auto). Options are "above"/"top" or "below"/"bottom".
     * @values auto, top, bottom
     */
    openDirection: {
      type: String,
      default: "auto",
    },

    /**
     * Set description for input.
     */
    description: {
      type: String,
      default: "",
    },

    /**
     * Show add new option button to the list.
     */
    addOption: {
      type: Boolean,
      default: false,
    },

    /**
     * Set input size.
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: "md",
    },

    /**
     * Allow search value in list.
     */
    searchable: {
      type: Boolean,
      default: true,
    },

    /**
     * Generates unique element id.
     * @ignore
     */
    id: {
      type: String,
      default: () => getRandomId(),
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },
  },

  data: () => ({
    isLoading: false,
    isOpen: null,
    isFocused: false,
    optionHeight: 0,
  }),

  computed: {
    wrapperId() {
      return `wrapper${this.id}`;
    },

    themeClass() {
      return getThemeClass();
    },

    icons: () => ({
      Close: require("@/components/_bit/IconSvg/icons/Close.svg"),
      Plus2: require("@/components/_bit/IconSvg/icons/Plus2.svg"),
      AngleDown: require("@/components/_bit/IconSvg/icons/Angle-down.svg"),
    }),

    multiselectClass() {
      return {
        error: this.error && !this.value.length,
        "multiselect-wrapper-disabled": this.disabled,
        "group-select": this.groupValues,
        "after-caret": this.isShownAfterCaretSlot,
        "size-sm": this.size === "sm",
        "size-md": this.size === "md",
        "size-lg": this.size === "lg",
      };
    },

    addOptionKeyCombination() {
      return isMac ? "(⌘ + Enter)" : "(Ctrl + Enter)";
    },

    isShownAfterCaretSlot() {
      return !!this.$slots["after-caret"];
    },

    isShownOptionSlot() {
      return !!this.$slots["option"];
    },

    isShownSingleLabelSlot() {
      return !!this.$slots["singleLabel"];
    },

    isShownSingleLabelAfterSlot() {
      return !!this.$slots["singleLabelAfter"];
    },

    labelClasses() {
      return {
        "label-color": this.isOpen,
        "error-label": this.error && !this.value.length,
      };
    },

    getTranslation() {
      return new I18nServiceDefault(this, { componentName: this.$options.name }).getTranslation;
    },

    i18n() {
      return {
        listIsEmpty: this.getTranslation("listIsEmpty"),
        noDataToShow: this.getTranslation("noDataToShow"),
        add: this.getTranslation("add"),
      };
    },

    selectValue: {
      get() {
        if (this.groupValues) {
          const option = this.options.find((option) => {
            return option[this.groupValues]?.find((item) => item[this.valueKey] === this.value);
          });

          return option?.[this.groupValues].find((option) => option.id === this.value);
        }

        return this.options.find((option) => option[this.valueKey] === this.value);
      },
      set(newItem) {
        this.$emit("input", newItem?.[this.valueKey]);
      },
    },

    multiselectContentWrapper() {
      return document.querySelector(`#${this.wrapperId} .multiselect__content-wrapper`);
    },

    iconSize() {
      const sizes = {
        sm: "sm",
        md: "md",
        lg: "lg",
      };

      return sizes[this.size];
    },
  },

  watch: {
    options: {
      handler: "onChangeOptions",
      deep: true,
    },

    isOpen: {
      handler: "onChangeOpenState",
      deep: true,
    },
  },

  created() {
    if (this.addOption) {
      document.addEventListener("keydown", this.onKeydownAddOption);
    }
  },

  mounted() {
    if (this.addOption) {
      this.multiselectContentWrapper.addEventListener("scroll", this.setAddOptionButtonPosition);
    }
  },

  beforeDestroy() {
    if (this.addOption) {
      document.removeEventListener("keydown", this.onKeydownAddOption);

      this.multiselectContentWrapper.removeEventListener("scroll", this.setAddOptionButtonPosition);
    }
  },

  methods: {
    setAddOptionButtonPosition() {
      const scrollBottomPosition = -this.multiselectContentWrapper.scrollTop;
      const defaultButtonPosition = 9;
      const addOptionPosition = (scrollBottomPosition + defaultButtonPosition) / PX_IN_REM;

      this.$refs.addOptionButton.$el.style.bottom = `${addOptionPosition}rem`;
    },

    onOpen() {
      this.$emit("open");
      this.isOpen = true;
      this.isFocused = true;
    },

    onMousedownRemoveValue() {
      this.selectValue = "";
    },

    onKeydownAddOption(event) {
      if (!this.isFocused) return;

      const isEnter = event.keyCode === 13;
      const isCtrl = event.ctrlKey;
      const isMeta = event.metaKey;

      if (isMeta && isEnter && isMac) {
        this.$emit("addOption");
      }

      if (isEnter && isCtrl && !isMac) {
        this.$emit("addOption");
      }
    },

    onClickAddOption() {
      this.$emit("addOption");
    },

    onChangeOptions() {
      const subOptions = document.querySelectorAll(".group-label");

      subOptions.forEach((subOption) => {
        const subOptionsParent = subOption.parentElement.parentElement;

        subOptionsParent.classList.add("multiselect__option");
        subOptionsParent.classList.add("multiselect__option--group");
        subOptionsParent.classList.add("multiselect__option--disabled");
      });
    },

    onClose() {
      this.isOpen = false;

      setTimeout(() => {
        this.isFocused = false;
      }, 100);

      this.$emit("close");
    },

    onInput() {
      this.$emit("change");
    },

    leftMarginForSubCategory(level) {
      const baseMargin = 1;

      if (level > 1) {
        return `margin-left: ${baseMargin * (level - 1)}rem`;
      }
    },

    removeValue(itemToRemove) {
      this.$emit("remove", itemToRemove);
    },

    onSearchChange: debounce(async function (query) {
      this.$emit("searchChange", query);
    }, 300),

    onChangeOpenState() {
      this.$nextTick(() => {
        this.optionHeight = this.$refs.select.$refs.list.firstChild.firstElementChild.clientHeight;
      });
    },

    hideInactiveElement(id) {
      let selectedId = {};

      if (this.groupValues !== "") {
        this.options.forEach((option) => {
          option[this.groupValues].forEach((item) => {
            if (item.id === id) selectedId = item;
          });
        });
      } else {
        selectedId = this.options.find((item) => item.id === id);
      }

      if (!selectedId) return;

      return selectedId.isHidden ? "cross-out-option" : "";
    },
  },
};
</script>

<i18n>
en:
  listIsEmpty: "List is empty."
  noDataToShow: "No data to show."
  add: Add
ru:
  listIsEmpty: "Список пуст."
  noDataToShow: "Нет данных."
  add: Добавить
ua:
  listIsEmpty: "Список порожній."
  noDataToShow: "Дані відсутні."
  add: Додати
</i18n>

<style lang="postcss" scoped>
.multiselect-block {
  @apply min-w-0;
  @apply relative;
}

.multiselect-wrapper {
  @apply flex w-full items-center;
  @apply box-border rounded-lg border border-gray-300 bg-white;

  &:hover {
    @apply border-gray-400;
    @apply transition duration-100 ease-in-out;
  }

  &:focus-within {
    @apply border-gray-500 ring-4 ring-gray-600 ring-opacity-15;
  }

  .multiselect {
    &-caret-down,
    &-caret-close {
      @apply -mt-1.5 mr-4;
      @apply cursor-pointer;
    }

    &-caret-close {
      @apply mr-1;
    }

    &--active {
      @apply z-[inherit];

      .icon {
        @apply rotate-180 transform transition-all duration-300;
      }
    }

    &.multiselect--above {
      &:deep(.multiselect__content-wrapper) {
        @apply bottom-full;
      }
    }

    &:deep() {
      @apply flex flex-row-reverse justify-between;
      @apply static min-h-full w-full overflow-hidden text-left;

      .multiselect {
        &__tags {
          @apply rounded-none border-0 bg-transparent;
          @apply min-h-full w-full overflow-hidden;
        }

        &__input {
          @apply font-normal text-gray-900;
          @apply p-0;
          @apply relative inline-block min-h-full w-full align-top;
          @apply border-none bg-white;

          &:focus {
            @apply shadow-none outline-none ring-0;
          }

          &::placeholder {
            @apply font-normal text-gray-400;
          }
        }

        &__placeholder {
          @apply inline-block;
          @apply whitespace-nowrap font-normal text-gray-400;
          @apply mb-0 w-0 pt-px;
        }

        &__content-wrapper {
          @apply rounded-lg border border-gray-300 shadow;
          @apply mb-2 mt-10;
          @apply absolute right-0;
          @apply z-50 block max-h-60 w-full overflow-auto;
          @apply bg-white;
          -webkit-overflow-scrolling: touch;
        }

        &__content {
          @apply inline-block list-none align-top;
          @apply m-0 min-w-full p-0;

          .multiselect__element:first-child {
            .multiselect__option--group {
              @apply !pt-4;
            }
          }
        }

        &__element {
          @apply block;
        }

        &__option {
          @apply text-base font-normal normal-case text-gray-900 no-underline;
          @apply relative block cursor-pointer align-middle;
          @apply px-3 py-2;

          &:hover {
            @apply bg-gray-100;
          }

          &:active,
          &--selected {
            @apply !bg-gray-200 font-medium;
          }

          &:disabled {
            @apply text-gray-900;
          }

          &--highlight {
            @apply bg-gray-100;
          }

          &--disabled {
            @apply pointer-events-none;
          }

          &--group {
            @apply bg-transparent;
            @apply !text-xs font-medium uppercase text-gray-500/[85];
            @apply pb-1 pt-6;
          }
        }

        &__single {
          @apply font-normal text-gray-900;
          @apply mb-px p-0;
          @apply relative inline-block w-full align-top;
          @apply border-none bg-white;
          @apply overflow-hidden text-ellipsis whitespace-nowrap;
        }
      }
    }
  }
}

.multiselect-wrapper-disabled {
  @apply border-gray-100 bg-gray-100 text-gray-900;

  &:hover {
    @apply border-gray-100;
  }

  &:focus-within {
    @apply border-gray-100 ring-0;
  }

  &:deep(.multiselect--disabled) {
    @apply rounded-lg bg-transparent;
    @apply pointer-events-none opacity-100;

    .multiselect__single {
      @apply bg-gray-100;
    }
  }
}

.icon {
  @apply transition-all duration-300;
}

.label {
  /* Match OurInput floating label (label sits above value/placeholder; reserve right for caret/clear). */
  @apply absolute z-10 font-normal text-gray-500;
  @apply left-4 right-14 truncate whitespace-nowrap;

  &-color {
    @apply font-normal text-gray-500;
    z-index: 9999;
  }
}

.multiselect-wrapper-disabled {
  @apply flex;
}

.size {
  &-sm {
    > .multiselect {
      &:deep() {
        @apply pt-6;

        .multiselect__option {
          @apply py-2;
        }

        .multiselect {
          &__tags {
            @apply mb-px pb-1.5 pl-4 pr-4 pt-1;
          }

          &__content-wrapper {
            @apply mt-8;
          }

          &__content {
            .multiselect__element {
              .multiselect__option--group {
                @apply !text-2xs;
                @apply pb-1 pt-6;
              }
            }
          }

          &__input {
            @apply mb-0 pt-0.5 text-sm;

            &::placeholder {
              @apply text-xs font-normal;
              @apply pt-0;
            }
          }

          &__single {
            @apply mt-1 text-sm;
          }

          &__placeholder {
            @apply text-xs;
          }

          &__option {
            @apply text-sm;
          }
        }
      }
    }

    > .label {
      @apply top-2 text-xs;

      &-color {
        @apply text-xs;
      }
    }
  }

  &-md {
    > .multiselect {
      &:deep() {
        @apply pt-7;

        .multiselect {
          &__tags {
            @apply mb-px pb-2 pl-4 pr-4 pt-1;
          }

          &__input {
            @apply mb-px text-base;

            &::placeholder {
              @apply text-base font-normal;
              @apply pt-px;
            }
          }

          &__single {
            @apply text-base;
          }

          &__placeholder {
            @apply text-base;
          }
        }
      }
    }

    > .label {
      @apply top-2.5 text-sm;

      &-color {
        @apply text-sm;
      }
    }
  }

  &-lg {
    > .multiselect {
      &:deep() {
        @apply pt-8;

        .multiselect {
          &__tags {
            @apply mb-px pb-2.5 pl-4 pr-4 pt-1;
          }

          &__content-wrapper {
            @apply mt-12;
          }

          &__content {
            .multiselect__element {
              .multiselect__option--group {
                @apply !text-sm;
                @apply pb-1 pt-6;
              }
            }
          }

          .multiselect__option {
            @apply py-4;
          }

          &__input {
            @apply mb-0 pt-0.5 text-lg;

            &::placeholder {
              @apply text-lg font-normal;
              @apply pt-0;
            }
          }

          &__single {
            @apply mt-1 text-lg;
          }

          &__placeholder {
            @apply text-lg;
          }

          &__option {
            @apply text-lg;
          }
        }
      }
    }

    > .label {
      @apply top-3 text-base;

      &-color {
        @apply text-base;
      }
    }
  }
}

.icon {
  @apply transition-all duration-300;
}

.error {
  @apply border-red-300;

  &:hover {
    @apply border-red-400;
  }

  &:focus-within {
    @apply border-red-500 ring-4 ring-red-100;
  }

  &-label {
    @apply text-red-500;
  }

  &-message {
    @apply text-xs font-normal text-red-500;
    @apply mt-2 pl-4;
  }
}

.description {
  @apply text-xs font-normal text-gray-500/[85];
  @apply pl-4 pt-2;
}

.group-label {
  @apply flex text-xs font-medium uppercase text-gray-500/[85];
}

.after-caret {
  @apply flex-col items-start md:flex-row md:items-center;

  .after-caret-slot {
    @apply pb-2.5 pl-4 pr-4 md:pb-0 md:pl-0;
  }
}

.multiselect-add {
  &-wrapper {
    @apply flex items-center justify-between;
    @apply cursor-pointer;
    @apply p-3;

    &:hover {
      @apply bg-gray-100;
    }

    &:active {
      @apply bg-gray-200 font-medium;
    }
  }

  &-title {
    @apply text-base font-medium text-gray-900;

    &-command {
      @apply text-gray-500;
    }
  }

  &-button {
    @apply rounded-full p-0;
  }

  &-icon {
    @apply bg-transparent;
    @apply p-0;

    &:deep(svg) {
      @apply !m-1;
    }

    &:deep(g [fill]) {
      @apply opacity-100;
    }
  }
}

.cross-out-option {
  ::v-deep(.multiselect__option--selected) {
    @apply hidden !important;
  }

  ::v-deep(.multiselect-single-slot) {
    @apply !line-through;
  }
}
</style>
